import { useMemo } from 'react';
import { computeBounds, Replay, sampleAt, speedColor } from '../model/Replay';

type Overlay = 'speed' | 'gear' | 'steer' | 'gas';

export function Minimap({
  replay,
  width = 600,
  height = 400,
  scrubT = null,
  overlay = 'speed',
  showLabels = true,
  showCheckpoints = true,
  showGhost = true,
  background = 'var(--bg-surface)',
  pad = 22,
}: {
  replay: Replay;
  width?: number;
  height?: number;
  scrubT?: number | null;
  overlay?: Overlay;
  showLabels?: boolean;
  showCheckpoints?: boolean;
  showGhost?: boolean;
  background?: string;
  pad?: number;
}) {
  const { ticks, checkpoints } = replay.data;

  const geo = useMemo(() => {
    const stride = Math.max(1, Math.floor(ticks.length / 500));
    const samples = [];
    for (let i = 0; i < ticks.length; i += stride) samples.push(ticks[i]);
    if (ticks.length && samples[samples.length - 1] !== ticks[ticks.length - 1]) samples.push(ticks[ticks.length - 1]);

    const { minX, maxX, minZ, maxZ } = computeBounds(replay);
    const w = maxX - minX || 1, h = maxZ - minZ || 1;
    const aspect = w / h;
    let viewW = width - pad * 2;
    let viewH = height - pad * 2;
    if (viewW / viewH > aspect) viewW = viewH * aspect;
    else viewH = viewW / aspect;
    const offsetX = (width - viewW) / 2;
    const offsetY = (height - viewH) / 2;

    const toScreen = (x: number, z: number): [number, number] => [
      offsetX + ((x - minX) / w) * viewW,
      offsetY + ((z - minZ) / h) * viewH,
    ];

    const topSpeed = replay.data.topSpeed;

    const segs: { x1: number; y1: number; x2: number; y2: number; color: string; t: number }[] = [];
    for (let i = 0; i < samples.length - 1; i++) {
      const a = samples[i], b = samples[i + 1];
      const [ax, ay] = toScreen(a.x, a.z);
      const [bx, by] = toScreen(b.x, b.z);
      let color = 'var(--accent)';
      if (overlay === 'speed') {
        color = speedColor((a.speed + b.speed) / 2, topSpeed);
      } else if (overlay === 'gear') {
        const gearColors = ['#3a3a3f', '#5fb5ff', '#00d4c4', '#7bf06b', '#ffe14a', '#ff8a3c'];
        color = gearColors[a.gear] || '#fff';
      } else if (overlay === 'steer') {
        if (a.steer < -0.05) color = '#0099ff';
        else if (a.steer > 0.05) color = '#ff8a3c';
        else color = '#7a7a7e';
      } else if (overlay === 'gas') {
        color = a.brake ? '#ff3355' : a.gas ? '#00ff88' : '#7a7a7e';
      }
      segs.push({ x1: ax, y1: ay, x2: bx, y2: by, color, t: a.t });
    }

    // Map each checkpoint to its nearest tick. Real GBX tick rates vary
    // (older replays sample at 10 Hz, newer at 50 Hz, custom maps mid-game can
    // skip frames) so a fixed tolerance misses; bsearch the nearest instead.
    // Empty ticks → no checkpoints to draw.
    const cps: { x: number; y: number; idx: number }[] = [];
    if (ticks.length > 0) {
      for (let idx = 0; idx < (checkpoints || []).length; idx++) {
        const cp = checkpoints[idx];
        let lo = 0, hi = ticks.length - 1;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (ticks[mid].t < cp.t) lo = mid + 1; else hi = mid;
        }
        // lo now points at the first tick >= cp.t — compare to the one before to
        // pick whichever is closer in time. ticks[lo] is guaranteed defined.
        const candA = ticks[lo];
        const candB = lo > 0 ? ticks[lo - 1] : candA;
        const tick = Math.abs(candA.t - cp.t) <= Math.abs(candB.t - cp.t) ? candA : candB;
        const [sx, sy] = toScreen(tick.x, tick.z);
        cps.push({ x: sx, y: sy, idx });
      }
    }

    const start = ticks[0];
    const finish = ticks[ticks.length - 1];
    const [startX, startY] = start ? toScreen(start.x, start.z) : [0, 0];
    const [finishX, finishY] = finish ? toScreen(finish.x, finish.z) : [0, 0];

    let ghost: { x: number; y: number; tick: typeof ticks[0] } | null = null;
    if (scrubT !== null && ticks.length) {
      const tk = sampleAt(replay, scrubT);
      const [gx, gy] = toScreen(tk.x, tk.z);
      ghost = { x: gx, y: gy, tick: tk };
    }

    return { segs, cps, startX, startY, finishX, finishY, ghost, hasStart: !!start };
  }, [replay, scrubT, overlay, width, height, pad, ticks, checkpoints]);

  return (
    <div
      style={{
        position: 'relative',
        background,
        borderRadius: 'var(--r-8)',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
      }}
    >
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <pattern id={`grid-${replay.meta.replayUid}`} width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" stroke="rgba(255,255,255,0.03)" strokeWidth="1" fill="none" />
          </pattern>
        </defs>
        <rect width={width} height={height} fill={`url(#grid-${replay.meta.replayUid})`} />

        <g>
          {geo.segs.map((s, i) => (
            <line key={'shadow-' + i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke="#1a1a1a" strokeWidth="9" strokeLinecap="round" />
          ))}
        </g>

        <g>
          {geo.segs.map((s, i) => (
            <line key={'seg-' + i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={s.color} strokeWidth="3" strokeLinecap="round" />
          ))}
        </g>

        {showCheckpoints &&
          geo.cps.map((cp, i) => (
            <g key={'cp-' + i}>
              <circle cx={cp.x} cy={cp.y} r="6" fill="var(--bg-surface)" stroke="var(--fg-2)" strokeWidth="1.5" />
              <text
                x={cp.x}
                y={cp.y + 3}
                textAnchor="middle"
                fontFamily="var(--font-mono)"
                fontSize="9"
                fontWeight="700"
                fill="var(--fg-1)"
              >
                {cp.idx + 1}
              </text>
            </g>
          ))}

        {geo.hasStart && (
          <g>
            <circle cx={geo.startX} cy={geo.startY} r="6" fill="var(--accent)" />
            <circle cx={geo.startX} cy={geo.startY} r="11" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.5" />
            {showLabels && (
              <text x={geo.startX + 14} y={geo.startY + 4} fontFamily="var(--font-mono)" fontSize="10" fontWeight="600" fill="var(--accent)">
                START
              </text>
            )}
          </g>
        )}

        {showGhost && geo.ghost && (
          <g>
            <circle cx={geo.ghost.x} cy={geo.ghost.y} r="14" fill="rgba(255,255,255,0.08)" />
            <circle cx={geo.ghost.x} cy={geo.ghost.y} r="8" fill="rgba(255,255,255,0.18)" />
            <circle cx={geo.ghost.x} cy={geo.ghost.y} r="4" fill="#fff" />
            <line
              x1={geo.ghost.x}
              y1={geo.ghost.y}
              x2={geo.ghost.x + Math.cos(geo.ghost.tick.yaw - Math.PI / 2) * 18}
              y2={geo.ghost.y + Math.sin(geo.ghost.tick.yaw - Math.PI / 2) * 18}
              stroke="#fff"
              strokeWidth="1.5"
            />
          </g>
        )}
      </svg>

      {showLabels && (
        <div
          style={{
            position: 'absolute',
            bottom: 8,
            left: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 10,
            color: 'var(--fg-3)',
            fontFamily: 'var(--font-mono)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--accent)' }} />
          {overlay} overlay
        </div>
      )}
    </div>
  );
}

// Multi-replay overlay minimap (for Compare page)
export function CompareMinimap({
  replays,
  width = 800,
  height = 540,
  visibleIds,
  pad = 26,
}: {
  replays: Replay[];
  width?: number;
  height?: number;
  visibleIds?: string[];
  pad?: number;
}) {
  const layers = useMemo(() => {
    const { minX, maxX, minZ, maxZ } = computeBounds(replays);
    const w = maxX - minX || 1, h = maxZ - minZ || 1;
    const aspect = w / h;
    let viewW = width - pad * 2;
    let viewH = height - pad * 2;
    if (viewW / viewH > aspect) viewW = viewH * aspect;
    else viewH = viewW / aspect;
    const offsetX = (width - viewW) / 2;
    const offsetY = (height - viewH) / 2;
    const toScreen = (x: number, z: number): [number, number] => [
      offsetX + ((x - minX) / w) * viewW,
      offsetY + ((z - minZ) / h) * viewH,
    ];

    return replays.map((r, idx) => {
      const stride = Math.max(1, Math.floor(r.data.ticks.length / 300));
      const path: string[] = [];
      for (let i = 0; i < r.data.ticks.length; i += stride) {
        const tk = r.data.ticks[i];
        const [x, y] = toScreen(tk.x, tk.z);
        path.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`);
      }
      const colors = ['#0099ff', '#ff6b35', '#b56bff', '#00ff88'];
      return {
        id: r.meta.replayUid,
        author: r.meta.author,
        color: colors[idx % colors.length],
        d: path.join(' '),
        visible: !visibleIds || visibleIds.includes(r.meta.replayUid),
      };
    });
  }, [replays, width, height, visibleIds, pad]);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block', background: 'var(--bg-surface)', borderRadius: 'var(--r-8)' }}
    >
      <defs>
        <pattern id="cmp-grid" width="50" height="50" patternUnits="userSpaceOnUse">
          <path d="M 50 0 L 0 0 0 50" stroke="rgba(255,255,255,0.03)" strokeWidth="1" fill="none" />
        </pattern>
      </defs>
      <rect width={width} height={height} fill="url(#cmp-grid)" />
      {layers.map(
        (l) =>
          l.visible && <path key={l.id} d={l.d} fill="none" stroke={l.color} strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />,
      )}
    </svg>
  );
}
